import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import { featureAccess } from "../../../permissions/access";
import { getBdcDispatchDay, getBdcDispatchNote, printBdcDispatchNotes, recordBdcDispatchNote } from "../../../services";
import { BdcDispatchDay, BdcDispatchDc, BdcDispatchNote } from "../../../types/bdc.type";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import BranchSelector from "../Branch/BranchSelector";
import { branchCodeOf } from "../Branch/branches";
import TestModeBanner from "../Branch/TestModeBanner";
import { useBdcBranchSelection, useBdcBranches } from "../Branch/useBdcBranches";
import { pcs, thaiDate, todayBkk } from "../format";
import KpiRow from "../KpiRow";
import { CARD_SX, TABLE_SX } from "../styles";
import DcTable from "./DcTable";
import PaperPreview from "./PaperPreview";
import { PdfMode, deliverPdf } from "./pdf";
import RecordPanel from "./RecordPanel";
import {
  FooterField,
  LineField,
  RecordDraft,
  buildRecordBody,
  emptyRecordDraft,
  printDcCodes,
  recordErrors,
  traysText,
  withFooterText,
  withLineText,
} from "./record";

const DISCARD_PROMPT = "มีตัวเลขที่ยังไม่บันทึก — ทิ้งการแก้ไขนี้?";

/**
 * BDC · ใบจ่ายสินค้า — one note per DC per ORDER date of a BDC branch (board Dispatch). The desk
 * ticks DCs and prints them as one PDF (2 DCs per A4, the first print creates the note), then
 * types back what the driver wrote: ขาด / เหลือ per line and the foot. Head office reads.
 */
export default function BdcDispatchScreen() {
  const role = useUserRole();
  const canWrite = featureAccess.bdcDispatchWrite(role);
  const [params, setParams] = useSearchParams();
  const { branches, ready: branchesReady } = useBdcBranches();
  const branch = useBdcBranchSelection(branches);

  const date = params.get("date") || todayBkk();
  const activeDc = params.get("dc");

  // One branch at a time: a viewer of one branch needs no pick; a viewer of two must pick.
  const companyId = branch.companyId ?? (branches.length === 1 ? branches[0].company_id : undefined);
  const needBranch = branchesReady && branches.length > 1 && !branch.companyId;
  const pickedBranch = branches.find((b) => b.company_id === companyId);

  const [day, setDay] = useState<BdcDispatchDay | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [printing, setPrinting] = useState<PdfMode | null>(null);
  const [note, setNote] = useState<BdcDispatchNote | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [draft, setDraft] = useState<RecordDraft>(emptyRecordDraft);
  const [saving, setSaving] = useState(false);
  const dayRequest = useRef(0);
  const noteRequest = useRef(0);
  /** Which branch + date the selection belongs to; a reload of the same day keeps it. */
  const selectionKey = useRef("");

  const activeRow = day?.dcs.find((dc) => dc.dc_code === activeDc) ?? null;
  const activeUuid = activeRow?.uuid ?? null;
  const errors = useMemo(
    () => (note ? recordErrors(note, draft) : { footer: {}, lines: {}, count: 0 }),
    [note, draft],
  );
  const body = useMemo(() => (note ? buildRecordBody(note, draft) : null), [note, draft]);
  const dirty = body !== null || errors.count > 0;
  const editable = canWrite;

  // ---- loading ----

  const loadDay = useCallback(async () => {
    if (!branchesReady || needBranch) return;
    const id = ++dayRequest.current;
    const key = `${companyId ?? ""}|${date}`;
    setLoading(true);
    // another day or branch: never leave the previous one on screen while loading
    if (selectionKey.current !== key) setDay(null);
    try {
      const {
        data: { data },
      } = await getBdcDispatchDay(date, companyId);
      if (id !== dayRequest.current) return;
      setDay(data);
      if (!data) return;
      const codes = data.dcs.map((dc) => dc.dc_code);
      // a new day starts with every DC ticked (as the board); a reload keeps the desk's ticks
      const sameDay = selectionKey.current === key;
      selectionKey.current = key;
      setSelected((current) => (sameDay ? new Set(codes.filter((code) => current.has(code))) : new Set(codes)));
    } catch {
      if (id === dayRequest.current) setDay(null);
    } finally {
      if (id === dayRequest.current) setLoading(false);
    }
  }, [branchesReady, needBranch, date, companyId]);

  const loadNote = useCallback(async (uuid: string | null) => {
    const id = ++noteRequest.current;
    setDraft(emptyRecordDraft());
    if (!uuid) {
      setNote(null);
      return;
    }
    setNoteLoading(true);
    setNote((current) => (current?.uuid === uuid ? current : null));
    try {
      const {
        data: { data },
      } = await getBdcDispatchNote(uuid);
      if (id === noteRequest.current) setNote(data);
    } catch {
      if (id === noteRequest.current) setNote(null);
    } finally {
      if (id === noteRequest.current) setNoteLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDay();
  }, [loadDay]);

  useEffect(() => {
    loadNote(activeUuid);
  }, [activeUuid, loadNote]);

  // Leaving the page with typed figures asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // ---- navigation (asks before throwing away a draft) ----

  const mayLeave = () => !dirty || window.confirm(DISCARD_PROMPT);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null) next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const pickDate = (value: Dayjs | null) => {
    if (!value || !value.isValid() || !mayLeave()) return;
    update({ date: value.format("YYYY-MM-DD"), dc: null });
  };

  const pickBranch = (value: string) => {
    if (!mayLeave()) return;
    update({ dc: null });
    branch.select(value);
  };

  const openDc = (dc: BdcDispatchDc) => {
    if (dc.dc_code === activeDc || !mayLeave()) return;
    update({ dc: dc.dc_code });
  };

  // ---- selection + print ----

  const toggle = (code: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const toggleAll = (checked: boolean) => setSelected(new Set(checked && day ? day.dcs.map((dc) => dc.dc_code) : []));

  const fileName = (codes: string[] | undefined) =>
    `bdc-dispatch-note-${pickedBranch ? branchCodeOf(pickedBranch) : "bdc"}-${date.replace(/-/g, "")}${
      codes && codes.length === 1 ? `-${codes[0]}` : ""
    }.pdf`;

  const print = async (mode: PdfMode, codes: string[] | undefined) => {
    if (!day || printing) return;
    setPrinting(mode);
    try {
      await deliverPdf(
        mode,
        () =>
          printBdcDispatchNotes({
            work_date: day.work_date,
            ...(companyId ? { company_id: companyId } : {}),
            ...(codes ? { dc_codes: codes } : {}),
          }),
        fileName(codes),
      );
    } finally {
      setPrinting(null);
      // a print creates / counts notes (and a 422 means the day moved): show the server's state
      await loadDay();
      if (activeUuid) loadNote(activeUuid);
    }
  };

  const selectedCodes = day ? printDcCodes(day.dcs, selected) : null;
  const printSelected = (mode: PdfMode) => {
    if (selectedCodes === null) return;
    print(mode, selectedCodes);
  };

  // ---- record ----

  const onFooterChange = (field: FooterField, text: string) => setDraft((d) => withFooterText(d, field, text));
  const onLineChange = (barcode: string, field: LineField, text: string) =>
    setDraft((d) => withLineText(d, barcode, field, text));

  const save = async () => {
    if (!note || errors.count > 0) return;
    setSaving(true);
    try {
      const {
        data: { data },
      } = await recordBdcDispatchNote(note.uuid, body ?? {});
      setNote(data);
      setDraft(emptyRecordDraft());
      loadDay();
    } catch {
      // interceptor reported it; the draft stays for the desk to fix
    } finally {
      setSaving(false);
    }
  };

  // ---- render ----

  const branchTitle = day?.branch_name || day?.branch_code || pickedBranch?.branch_name || "";
  const printedCount = day?.dcs.filter((dc) => dc.status !== "not_printed").length ?? 0;
  const recordedCount = day?.dcs.filter((dc) => dc.status === "recorded").length ?? 0;
  const shortCount = day?.dcs.filter((dc) => dc.has_short).length ?? 0;
  const changedCount = day?.dcs.filter((dc) => dc.is_changed).length ?? 0;
  const selectedCount = day ? day.dcs.filter((dc) => selected.has(dc.dc_code)).length : 0;
  const printBlocked = dirty ? "บันทึกหรือยกเลิกการแก้ไขก่อนพิมพ์" : undefined;

  return (
    <Box sx={{ padding: "8px 0", ...TABLE_SX }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            ใบจ่ายสินค้า
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {day
              ? [branchTitle, `${day.weekday} ${thaiDate(day.work_date)} (วันสั่ง)`, canWrite ? "" : "บัญชีนี้ดูได้อย่างเดียว"]
                  .filter(Boolean)
                  .join(" · ")
              : "1 ใบต่อ DC ต่อวันสั่ง รวมทุกรอบ · พิมพ์ให้คนขับ แล้วบันทึกขาด/เหลือและท้ายใบกลับเข้าระบบ"}
          </Typography>
        </Box>
        {canWrite && day && day.dcs.length > 0 && (
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              disabled={selectedCount === 0 || Boolean(printBlocked) || (printing !== null && printing !== "download")}
              loading={printing === "download"}
              title={printBlocked}
              onClick={() => printSelected("download")}
            >
              ดาวน์โหลด PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              disabled={selectedCount === 0 || Boolean(printBlocked) || (printing !== null && printing !== "open")}
              loading={printing === "open"}
              title={printBlocked}
              onClick={() => printSelected("open")}
            >
              พิมพ์ที่เลือก ({selectedCount})
            </Button>
          </Stack>
        )}
      </Stack>

      <Stack gap={2}>
        <Card sx={{ ...CARD_SX, p: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center" sx={{ px: 1, py: 0.5 }}>
              <BranchSelector branches={branches} value={branch.selected} onChange={pickBranch} allowAll={false} />
              <DatePicker
                label="วันสั่ง"
                value={dayjs(date)}
                format="DD/MM/YYYY"
                onChange={pickDate}
                slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
              />
              <Typography variant="body2" color="text.secondary">
                {thaiDate(date)} · วันของไฟล์ ORDS (ไม่ใช่วันส่ง)
              </Typography>
            </Stack>
          </LocalizationProvider>
        </Card>

        <TestModeBanner branches={pickedBranch ? [pickedBranch] : []} />

        {needBranch && <Alert severity="info">เลือกสาขาก่อน — ใบจ่ายแยกกันต่อสาขา BDC</Alert>}

        {loading && <LinearProgress />}

        {day && !needBranch && (
          <>
            {day.dcs.length > 0 && (
              <KpiRow
                items={[
                  { label: "DC", value: pcs(day.dcs.length), sub: day.title },
                  { label: "เบิกรวม (ชิ้น)", value: pcs(day.totals.issued_qty), sub: `ถาด ${traysText(day.totals.trays, day.totals.pieces)}` },
                  { label: "พิมพ์แล้ว", value: `${printedCount}/${day.dcs.length}` },
                  { label: "บันทึกแล้ว", value: `${recordedCount}/${day.dcs.length}` },
                  ...(shortCount > 0 ? [{ label: "DC ที่มีขาด", value: pcs(shortCount), sub: "สร้าง CN ได้จากใบจ่ายของ DC นั้น", tone: "warn" as const }] : []),
                ]}
              />
            )}
            {changedCount > 0 && (
              <Alert severity="warning">
                ยอดเบิกของ {changedCount} DC เปลี่ยนหลังพิมพ์ (นำเข้าไฟล์ใหม่) — ใบที่ยังไม่บันทึกพิมพ์ซ้ำเพื่อให้ตรงกับยอดล่าสุด
              </Alert>
            )}

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
              <Box sx={{ flex: "1 1 380px", minWidth: 0 }}>
                <DcTable
                  day={day}
                  selected={selected}
                  selectable={canWrite}
                  activeDc={activeDc}
                  onToggle={toggle}
                  onToggleAll={toggleAll}
                  onOpen={openDc}
                />
              </Box>
              <Box sx={{ flex: "1 1 520px", minWidth: 0 }}>
                {note && activeRow ? (
                  <PaperPreview note={note} title={day.title} />
                ) : activeRow && !activeRow.uuid ? (
                  <Card sx={{ ...CARD_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {activeRow.dc_code} {activeRow.dc_name} · ยังไม่พิมพ์
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ใบจ่ายถูกสร้างตอนพิมพ์ครั้งแรก — พิมพ์ใบให้คนขับก่อน แล้วจึงบันทึกขาด/เหลือกลับได้
                    </Typography>
                    {canWrite && (
                      <Box>
                        <Button
                          variant="contained"
                          startIcon={<PrintIcon />}
                          loading={printing === "open"}
                          disabled={printing !== null || Boolean(printBlocked)}
                          onClick={() => print("open", [activeRow.dc_code])}
                        >
                          พิมพ์ใบของ {activeRow.dc_code}
                        </Button>
                      </Box>
                    )}
                  </Card>
                ) : (
                  !noteLoading && (
                    <Card sx={{ ...CARD_SX, p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        กดแถว DC เพื่อดูตัวอย่างใบ{canWrite ? " และบันทึกใบจ่ายกลับ" : ""}
                      </Typography>
                    </Card>
                  )
                )}
              </Box>
            </Box>

            {noteLoading && !note && <LinearProgress />}
            {note && activeRow && (
              <RecordPanel
                note={note}
                draft={draft}
                errors={errors}
                editable={editable}
                dirty={dirty}
                saving={saving}
                onFooterChange={onFooterChange}
                onLineChange={onLineChange}
                onSave={save}
                onDiscard={() => setDraft(emptyRecordDraft())}
              />
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}
